import { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Chip,
  Stack,
  LinearProgress,
  Box,
} from '@mui/material';
import { getMySubscription } from '../../services/subscriptionService';
import { errorNotification } from '../../services/notificationService';

const tierColors = {
  beta: 'info',
  bronze: 'warning',
  silver: 'default',
  gold: 'success',
};

function formatStorage(mb) {
  if (mb >= 1_048_576) return `${(mb / 1_048_576).toFixed(0)} TB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(0)} GB`;
  return `${mb} MB`;
}

function formatPrice(cents) {
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(2)}/mo`;
}

export default function SubscriptionCard({ onManagePlan }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMySubscription()
      .then(setSubscription)
      .catch(() => errorNotification('Failed to load subscription'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Card><CardContent><LinearProgress /></CardContent></Card>;
  if (!subscription) return null;

  const usagePercent = subscription.storageLimitMb > 0
    ? (subscription.usedStorageMb / subscription.storageLimitMb) * 100
    : 0;

  return (
    <Card>
      <CardHeader
        title="Subscription"
        action={
          <Chip
            label={subscription.displayName}
            color={tierColors[subscription.tier] || 'default'}
            size="small"
          />
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            <strong>Plan:</strong> {subscription.displayName} — {formatPrice(subscription.monthlyPriceCents)}
          </Typography>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Storage:</strong> {formatStorage(subscription.usedStorageMb)} / {formatStorage(subscription.storageLimitMb)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(usagePercent, 100)}
              color={usagePercent > 90 ? 'error' : usagePercent > 70 ? 'warning' : 'primary'}
            />
          </Box>
          <Button variant="outlined" onClick={onManagePlan}>
            Manage Plan
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
